import { useState, useEffect, useCallback, useRef } from 'react'
import { safariMemoryManager } from '../helpers/SafariMemoryManager'

interface MemoryStats {
  usedMB: string
  maxMB: string
  percentage: number
  canvasCount: number
  isSafari: boolean
  isMemoryPressureHigh: boolean
}

interface PageMemoryInfo {
  pageNumber: number
  width: number
  height: number
  pixelRatio: number
  memoryUsage: number
  timestamp: number
}

export const useMemoryManagement = () => {
  const [memoryStats, setMemoryStats] = useState<MemoryStats>({
    usedMB: '0.0',
    maxMB: '0.0',
    percentage: 0,
    canvasCount: 0,
    isSafari: false,
    isMemoryPressureHigh: false,
  })

  const pageMemoryMap = useRef<Map<number, PageMemoryInfo>>(new Map())
  const updateTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Update memory stats periodically
  const updateMemoryStats = useCallback(() => {
    const usage = safariMemoryManager.getMemoryUsage()
    const capabilities = safariMemoryManager.getBrowserCapabilities()

    setMemoryStats({
      usedMB: (usage.used / 1024 / 1024).toFixed(1),
      maxMB: (usage.max / 1024 / 1024).toFixed(0),
      percentage: usage.percentage,
      canvasCount: usage.canvasCount,
      isSafari: capabilities.isSafari,
      isMemoryPressureHigh: usage.percentage > 80,
    })
  }, [])

  // Start periodic updates
  useEffect(() => {
    updateMemoryStats()
    updateTimer.current = setInterval(updateMemoryStats, 2000) // Update every 2 seconds

    return () => {
      if (updateTimer.current) {
        clearInterval(updateTimer.current)
      }
    }
  }, [updateMemoryStats])

  const addRenderedPage = useCallback(
    (pageNumber: number, width: number, height: number, pixelRatio: number) => {
      const memoryUsage = width * height * pixelRatio * pixelRatio * 4

      const pageInfo: PageMemoryInfo = {
        pageNumber,
        width,
        height,
        pixelRatio,
        memoryUsage,
        timestamp: Date.now(),
      }

      pageMemoryMap.current.set(pageNumber, pageInfo)

      // Register canvas with SafariMemoryManager - this updates the memory tracking
      safariMemoryManager.getCanvas(width, height, pageNumber)

      // Force immediate update to reflect changes
      setTimeout(updateMemoryStats, 100)
    },
    [updateMemoryStats]
  )

  const removeRenderedPage = useCallback(
    (pageNumber: number) => {
      const pageInfo = pageMemoryMap.current.get(pageNumber)
      if (pageInfo) {
        // Release canvas from SafariMemoryManager - this updates the memory tracking
        safariMemoryManager.releaseCanvas(pageNumber)
        pageMemoryMap.current.delete(pageNumber)

        // Force immediate update to reflect changes
        setTimeout(updateMemoryStats, 100)
      }
    },
    [updateMemoryStats]
  )

  const getOptimalRenderSettings = useCallback(
    (
      requestedWidth: number,
      requestedHeight: number
    ): {
      width: number
      height: number
      scale: number
      devicePixelRatio: number
    } => {
      return safariMemoryManager.getOptimalRenderSettings(requestedWidth, requestedHeight)
    },
    []
  )

  const canRenderPage = useCallback(
    (width: number, height: number, pixelRatio: number = 1): boolean => {
      return safariMemoryManager.canAllocateCanvas(width, height, pixelRatio)
    },
    []
  )

  const forceCleanup = useCallback(() => {
    // Remove old pages first
    const now = Date.now()
    const oldThreshold = 60000 // 1 minute

    const pagesToRemove: number[] = []
    pageMemoryMap.current.forEach((info, pageNumber) => {
      if (now - info.timestamp > oldThreshold) {
        pagesToRemove.push(pageNumber)
      }
    })

    pagesToRemove.forEach((pageNumber) => {
      removeRenderedPage(pageNumber)
    })

    // Force memory stats update
    setTimeout(updateMemoryStats, 100)
  }, [removeRenderedPage, updateMemoryStats])

  const getPageMemoryInfo = useCallback((pageNumber: number): PageMemoryInfo | undefined => {
    return pageMemoryMap.current.get(pageNumber)
  }, [])

  const getAllPagesMemoryInfo = useCallback((): PageMemoryInfo[] => {
    return Array.from(pageMemoryMap.current.values())
  }, [])

  // Automatic cleanup when memory pressure is high
  useEffect(() => {
    if (memoryStats.isMemoryPressureHigh) {
      forceCleanup()
    }
  }, [memoryStats.isMemoryPressureHigh, forceCleanup])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pageMemoryMap.current.clear()
      if (updateTimer.current) {
        clearInterval(updateTimer.current)
      }
    }
  }, [])

  return {
    // Memory stats - now properly synced with SafariMemoryManager
    memoryStats,

    // Page management
    addRenderedPage,
    removeRenderedPage,

    // Optimization helpers
    getOptimalRenderSettings,
    canRenderPage,

    // Cleanup functions
    forceCleanup,

    // Memory info
    getPageMemoryInfo,
    getAllPagesMemoryInfo,

    // Legacy compatibility (for existing code) - now returns actual values
    getMemoryUsageMB: () => memoryStats.usedMB,
    renderedPagesCount: memoryStats.canvasCount,
  }
}
